import axios from 'axios';
import { logOutClientCleanUp, storePostLoginRedirectPath } from '../utils/Utilities';
import { logoutUser } from '../redux/slice/userSlice'; // Import the logoutUser action
import { store } from '../redux/store'; // Import the Redux store

// Create API instance with sessionId (for requests that need sessionId)
const MAX_RETRIES = 2; // You can increase this if you want to retry more times
const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: process.env.REACT_APP_API_URL_TIMEOUT || 10000, // Timeout after 10 seconds (in milliseconds)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor for apiInstance - adds sessionId to headers
apiInstance.interceptors.request.use((config) => {
  return config;
}, (error) => {
  return Promise.reject(error);
});


// Create API instance with both sessionId and Firebase token
const apiInstanceWithToken = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: process.env.REACT_APP_API_URL_TIMEOUT || 10000, // Timeout after 10 seconds (in milliseconds)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add Authorization header to apiInstanceWithToken
apiInstanceWithToken.interceptors.request.use(async (config) => {
  // Get token from IndexedDB or sessionStorage (depending on your logic)
  if (process.env.REACT_APP_ENV === 'Development') {
    let token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for retrying requests when receiving 401 or 403 errors
apiInstanceWithToken.interceptors.response.use(
  (response) => response, // Pass through the response if everything is fine
  async (error) => {
    const originalRequest = error.config; // The original request that failed

    // Prevent infinite retries by checking retry count
    if (originalRequest._retryCount >= MAX_RETRIES) {
      // If we've hit max retries, reject and force logout
      //showErrorToast(strings.sessionExpired || "உங்கள் அமர்வு காலாவதியாகிவிட்டது. தயவுசெய்து மீண்டும் உள்நுழையவும்.");
      const currentPath = window.location.pathname + window.location.search;
      storePostLoginRedirectPath(currentPath);

      await logOutClientCleanUp('lite-logout');
      store.dispatch(logoutUser());
      return Promise.reject(error);
    }

    // Initialize retry count if it's not already set
    originalRequest._retryCount = originalRequest._retryCount || 0;

    // 1. Check if the error status is 600 (or any other status you want to stop retrying for)
    if (error.response && error.response.status === 600) {
      const currentPath = window.location.pathname + window.location.search;
      storePostLoginRedirectPath(currentPath);
      // Dispatch the logout action from Redux to trigger the logout logic
      await logOutClientCleanUp('lite-logout');
      store.dispatch(logoutUser());
      //showErrorToast(strings.sessionExpired || "உங்கள் அமர்வு காலாவதியாகிவிட்டது. தயவுசெய்து மீண்டும் உள்நுழையவும்.");
      // Reject the request immediately when status 600 is received
      return Promise.reject(error);  // This prevents retries for 600 errors
    }

    // 2. Handle Unauthorized (401) and Forbidden (403)
    if (error.response && (error.response.status === 401 || error.response.status === 498)) {
      // Increment retry count
      originalRequest._retryCount++;

      console.error(`Authentication failed (401/498). Retrying... Attempt: ${originalRequest._retryCount}`);

      const currentPath = window.location.pathname + window.location.search;
      storePostLoginRedirectPath(currentPath);
      // Force logout and redirect to login  
      await logOutClientCleanUp('lite-logout');
      store.dispatch(logoutUser());
      //showErrorToast(strings.sessionExpired || "உங்கள் அமர்வு காலாவதியாகிவிட்டது. தயவுசெய்து மீண்டும் உள்நுழையவும்.");
      return Promise.reject(error); // Reject the original request
    }

    // 3. Handle Server Error (500 or others) and reject without retrying
    if (error.response && error.response.status >= 500) {
      console.error('Server error occurred:', error);
      // No retries for server errors (5xx)
      return Promise.reject(error);
    }

    // 3. For any other error (e.g., network issues, unexpected status codes), reject the promise and don't retry
    return Promise.reject(error);
  }
);

export { apiInstance, apiInstanceWithToken }; // Export both instances for use in your app
